import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PriceAge from '../PriceAge';
import { formatNumber, formatDecimal } from '../../utils/numberFormat';

export interface OrderBookEntry {
  price: number;
  quantity: number;
  total: number;
}

interface BidAskProps {
  height?: number;
  priceData?: any | null;
  /** Start in side-by-side view instead of the price ladder. Pair defaults to it
      (10 levels per side fit only that way); grid keeps the ladder. The
      in-header toggle still switches either one at runtime. */
  defaultSplitView?: boolean;
}

const BidAskWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height > 0 ? `${$height}px` : '100%'};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 0;
  max-height: 100%;
`;

const BidAsk: React.FC<BidAskProps> = ({ height = 500, priceData, defaultSplitView = false }) => {
  const [asks, setAsks] = useState<OrderBookEntry[]>([]);
  const [bids, setBids] = useState<OrderBookEntry[]>([]);
  // Price ladder vs side-by-side. Derivatives quote ~10 levels per side, and
  // the ladder spends half of every row on "--" placeholders, so only the split
  // view fits both sides in one screen. Which one starts is up to the caller
  // (`defaultSplitView`); the header toggle overrides it per session.
  const [splitView, setSplitView] = useState(defaultSplitView);

  // Process price data from parent component
  useEffect(() => {
    if (!priceData) {
      setBids([]);
      setAsks([]);
      return;
    }

    // Extract bid data (bid_price_1 through bid_price_10)
    // Keep previous values if new data is null/undefined
    setBids(prevBids => {
      const bidEntries: OrderBookEntry[] = [];
      let bidTotal = 0;

      for (let i = 1; i <= 10; i++) {
        const newBidPrice = priceData[`bid_price_${i}`];
        const newBidQuantity = priceData[`bid_quantity_${i}`];

        // Use new value if available, otherwise use previous value for this level
        const bidPrice = (newBidPrice !== null && newBidPrice !== undefined && newBidPrice > 0)
          ? newBidPrice
          : (prevBids[i - 1]?.price ?? null);
        const bidQuantity = (newBidQuantity !== null && newBidQuantity !== undefined && newBidQuantity > 0)
          ? newBidQuantity
          : (prevBids[i - 1]?.quantity ?? null);

        if (bidPrice !== null && bidPrice > 0 && bidQuantity !== null && bidQuantity > 0) {
          bidTotal += bidQuantity;
          bidEntries.push({
            price: bidPrice,
            quantity: bidQuantity,
            total: bidTotal
          });
        }
      }

      // Sort bids descending by price (highest first)
      bidEntries.sort((a, b) => b.price - a.price);

      // Only update if bids actually changed
      if (prevBids.length === bidEntries.length &&
        prevBids.every((bid, idx) =>
          bid.price === bidEntries[idx]?.price &&
          bid.quantity === bidEntries[idx]?.quantity
        )) {
        return prevBids; // No change
      }
      return bidEntries;
    });

    // Extract ask data (ask_price_1 through ask_price_10)
    // Keep previous values if new data is null/undefined
    setAsks(prevAsks => {
      const askEntries: OrderBookEntry[] = [];
      let askTotal = 0;

      for (let i = 1; i <= 10; i++) {
        const newAskPrice = priceData[`ask_price_${i}`];
        const newAskQuantity = priceData[`ask_quantity_${i}`];

        const askPrice = (newAskPrice !== null && newAskPrice !== undefined && newAskPrice > 0)
          ? newAskPrice
          : (prevAsks[i - 1]?.price ?? null);
        const askQuantity = (newAskQuantity !== null && newAskQuantity !== undefined && newAskQuantity > 0)
          ? newAskQuantity
          : (prevAsks[i - 1]?.quantity ?? null);

        if (askPrice !== null && askPrice > 0 && askQuantity !== null && askQuantity > 0) {
          askTotal += askQuantity;
          askEntries.push({
            price: askPrice,
            quantity: askQuantity,
            total: askTotal
          });
        }
      }

      // Sort asks ascending by price (lowest first)
      askEntries.sort((a, b) => a.price - b.price);

      // Only update if asks actually changed
      if (prevAsks.length === askEntries.length &&
        prevAsks.every((ask, idx) =>
          ask.price === askEntries[idx]?.price &&
          ask.quantity === askEntries[idx]?.quantity
        )) {
        return prevAsks; // No change
      }
      return askEntries;
    });
  }, [priceData]);

  const bestAsk = asks.length > 0 ? asks[0].price : 0;
  const bestBid = bids.length > 0 ? bids[0].price : 0;
  const spread = formatDecimal(bestAsk - bestBid, 2);
  const spreadPercent = bestBid > 0 ? formatDecimal(((bestAsk - bestBid) / bestBid) * 100, 3) : '0.000';

  const maxQty = Math.max(
    ...asks.map(a => a.quantity),
    ...bids.map(b => b.quantity),
    1
  );

  // Continuous price ladder: asks descending then spread then bids descending
  const askRows = [...asks].reverse(); // highest first
  const bidRows = bids; // highest first

  return (
    <BidAskWrapper $height={height}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* Header. The layout toggle rides in the header row so it costs no
            vertical space of its own. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 8px 1fr 1fr', fontSize: '10px', color: '#848E9C', fontWeight: 600, padding: '2px 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <div style={{ textAlign: 'left' }}>Qty</div>
          <div style={{ textAlign: 'right' }}>Ask</div>
          <div />
          <div style={{ textAlign: 'left' }}>Bid</div>
          <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
            <PriceAge priceData={priceData} />
            <span>Qty</span>
            <span
              role="button"
              title={splitView ? 'Switch to price-ladder view' : 'Switch to side-by-side view'}
              onClick={() => setSplitView(v => !v)}
              style={{ cursor: 'pointer', color: splitView ? '#F0B90B' : '#848E9C', fontSize: '11px', lineHeight: 1, userSelect: 'none' }}
            >
              {splitView ? '⇅' : '⇄'}
            </span>
          </div>
        </div>
        {/* Side-by-side: ask levels on the left, bid levels on the right, one
            row per depth index. The spread bar stands between the two columns
            and reads vertically, so it separates the sides instead of cutting
            the ladder in half. */}
        {splitView ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '4px' }}>
            <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
              {askRows.map((row, index) => {
                const fillPct = (row.quantity / maxQty) * 100;
                return (
                  <div key={`s-ask-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '2px 4px', position: 'relative', fontSize: '11px', fontFamily: "'Courier New', monospace" }}>
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${fillPct}%`, background: 'rgba(246, 70, 93, 0.1)', zIndex: 0 }} />
                    <div style={{ textAlign: 'left', color: '#E0E0E0', position: 'relative', zIndex: 1 }}>{formatNumber(row.quantity)}</div>
                    <div style={{ textAlign: 'right', color: '#F6465D', fontWeight: 600, position: 'relative', zIndex: 1 }}>{formatDecimal(row.price, 2)}</div>
                  </div>
                );
              })}
            </div>
            {/* Vertical spread bar */}
            <div style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '8px 4px',
              background: 'rgba(240, 185, 11, 0.08)',
              border: '1px solid rgba(240, 185, 11, 0.25)',
              borderRadius: '3px',
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: "'Courier New', monospace",
              color: '#E0E0E0',
              // Rotate the text with the bar: upright glyphs stacked top-down.
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
            }}>
              <span style={{ color: '#F0B90B' }}>↕</span>
              <span>{spread}</span>
              <span style={{ color: '#848E9C', fontWeight: 400 }}>({spreadPercent}%)</span>
            </div>
            <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
              {bidRows.map((row, index) => {
                const fillPct = (row.quantity / maxQty) * 100;
                return (
                  <div key={`s-bid-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '2px 4px', position: 'relative', fontSize: '11px', fontFamily: "'Courier New', monospace" }}>
                    <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${fillPct}%`, background: 'rgba(14, 203, 129, 0.1)', zIndex: 0 }} />
                    <div style={{ textAlign: 'left', color: '#0ECB81', fontWeight: 600, position: 'relative', zIndex: 1 }}>{formatDecimal(row.price, 2)}</div>
                    <div style={{ textAlign: 'right', color: '#E0E0E0', position: 'relative', zIndex: 1 }}>{formatNumber(row.quantity)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
        /* Continuous rows */
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Ask rows */}
          {askRows.map((row, index) => {
            const fillPct = (row.quantity / maxQty) * 100;
            return (
              <div key={`ask-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 8px 1fr 1fr', padding: '2px 4px', position: 'relative', fontSize: '11px', fontFamily: "'Courier New', monospace" }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${fillPct / 2}%`, background: 'rgba(246, 70, 93, 0.1)', zIndex: 0 }} />
                <div style={{ textAlign: 'left', color: '#E0E0E0', position: 'relative', zIndex: 1 }}>{formatNumber(row.quantity)}</div>
                <div style={{ textAlign: 'right', color: '#F6465D', fontWeight: 600, position: 'relative', zIndex: 1 }}>{formatDecimal(row.price, 2)}</div>
                <div />
                <div style={{ textAlign: 'left', color: '#0ECB81', position: 'relative', zIndex: 1, opacity: 0.3 }}>--</div>
                <div style={{ textAlign: 'right', color: '#E0E0E0', position: 'relative', zIndex: 1, opacity: 0.3 }}>--</div>
              </div>
            );
          })}
          {/* Spread */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '6px 8px', margin: '2px 0', fontSize: '11px', color: '#E0E0E0', background: 'rgba(240, 185, 11, 0.08)', border: '1px solid rgba(240, 185, 11, 0.25)', borderRadius: '3px', flexShrink: 0, fontWeight: 600, fontFamily: "'Courier New', monospace" }}>
            <span style={{ color: '#F0B90B' }}>↕</span>
            <span>{spread}</span>
            <span style={{ color: '#848E9C', fontWeight: 400 }}>({spreadPercent}%)</span>
          </div>
          {/* Bid rows */}
          {bidRows.map((row, index) => {
            const fillPct = (row.quantity / maxQty) * 100;
            return (
              <div key={`bid-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 8px 1fr 1fr', padding: '2px 4px', position: 'relative', fontSize: '11px', fontFamily: "'Courier New', monospace" }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${fillPct / 2}%`, background: 'rgba(14, 203, 129, 0.1)', zIndex: 0 }} />
                <div style={{ textAlign: 'left', color: '#E0E0E0', position: 'relative', zIndex: 1, opacity: 0.3 }}>--</div>
                <div style={{ textAlign: 'right', color: '#F6465D', position: 'relative', zIndex: 1, opacity: 0.3 }}>--</div>
                <div />
                <div style={{ textAlign: 'left', color: '#0ECB81', fontWeight: 600, position: 'relative', zIndex: 1 }}>{formatDecimal(row.price, 2)}</div>
                <div style={{ textAlign: 'right', color: '#E0E0E0', position: 'relative', zIndex: 1 }}>{formatNumber(row.quantity)}</div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </BidAskWrapper>
  );

};

export default BidAsk;

